'use client';

import { Drawer } from 'antd';
import { createContext, type CSSProperties, type ReactNode, useCallback, useState } from 'react';

export const SidebarActionsPortalContext = createContext<HTMLElement | null>(null);

type SidebarPropsType = {
  isVisible: boolean;
  title?: ReactNode;
  placement?: 'right' | 'top' | 'bottom' | 'left';
  width?: string | number;
  content: ReactNode;
  footer?: ReactNode;
  className?: string;
  headerClassName?: string;
  destroyOnHidden?: boolean;
  zIndex?: number;
  portalToBody?: boolean;
  onClose?: () => void;
};

const Sidebar = ({
  isVisible,
  title,
  destroyOnHidden = true,
  placement = 'right',
  width = '50%',
  content,
  zIndex,
  portalToBody = false,
  className,
  headerClassName,
  footer,
  onClose,
}: SidebarPropsType) => {
  const drawerStyle: CSSProperties = placement === 'left' || placement === 'right' ? { width } : {};
  const [actionsPortalTarget, setActionsPortalTarget] = useState<HTMLDivElement | null>(null);

  const setActionsPortalRef = useCallback((node: HTMLDivElement | null) => {
    setActionsPortalTarget(node);
  }, []);

  return (
    <Drawer
      title={title}
      getContainer={portalToBody ? undefined : false}
      push={false}
      rootStyle={{ position: 'fixed', inset: 0 }}
      zIndex={zIndex ?? undefined}
      styles={{
        wrapper: {
          marginInlineStart: placement === 'right' ? 'auto' : undefined,
          height: '100%',
          maxHeight: '100dvh',
          ...drawerStyle,
        },
        section: { height: '100dvh', maxHeight: '100dvh' },
      }}
      className={className}
      classNames={{ header: headerClassName }}
      extra={<div ref={setActionsPortalRef} className="sidebar__actions-portal-target" />}
      destroyOnHidden={destroyOnHidden}
      placement={placement}
      footer={footer}
      onClose={onClose}
      open={isVisible}
    >
      <SidebarActionsPortalContext.Provider value={actionsPortalTarget}>
        {content}
      </SidebarActionsPortalContext.Provider>
    </Drawer>
  );
};

export default Sidebar;
